import * as Sentry from "@sentry/react";
import PropTypes from "prop-types";
import React from "react";
import { connect } from "react-redux";
import { sortBy } from "lodash";
import { getUserRole } from "shared/selectors";
import { Button, Card, Divider, Icon, Message } from "semantic-ui-react";
import { FormattedMessage } from "react-intl";
import {
  getResource,
  SUB_LINE_ITEM_RESOURCE
} from "shared/actions/actionLocator";
import {
  getAffectingSubLineItems,
  getSubLineItemsById
} from "shared/selectors/unit/subLineItems";
import { getActivatedProductLine } from "shared/selectors/unit/productLines";
import { Hint } from "shared/components/hint/Hint";
import { defaultRichTextTags } from "builder_portal/helpers/defaultRichTextTags";
import { withRouter } from "../../helpers/withRouter";
import SubLineItemPicker from "./pickers/SubLineItemPicker";
import ProductOption from "./ProductOption";
import { Unless } from "../elements/Conditions";
import "./productOptions.scss";

class ProductOptions extends React.Component {
  static propTypes = {
    i18n: PropTypes.object.isRequired,
    subLineItem: PropTypes.object.isRequired,
    userRole: PropTypes.string.isRequired,
    priceField: PropTypes.string,
    renderMissingMappingIcon: PropTypes.bool,
    renderMetaThumbs: PropTypes.bool,
    renderQuantityLabel: PropTypes.bool
  };

  /** Sorting * */
  static generateInitialOrder(subLineItemOptions) {
    return subLineItemOptions.reduce((order, option, idx) => {
      const base = !option.isValidOption
        ? 1000
        : !option.isRecommendable
        ? 500
        : 0;
      order[option.id] = option.isBuyerSelection ? -1 : base + idx;
      return order;
    }, {});
  }

  constructor(props) {
    super(props);

    this.state = {
      order: ProductOptions.generateInitialOrder(props.subLineItem.options)
    };
  }

  componentDidUpdate(prevProps) {
    const { subLineItem } = this.props;
    if (subLineItem.id !== prevProps.subLineItem.id) {
      // re-render only if new SLI ID has arrived
      // eslint-disable-next-line react/no-did-update-set-state
      this.setState({
        order: ProductOptions.generateInitialOrder(subLineItem.options)
      });
    }
    return null; // do not re-render
  }

  renderSelectionHint = messageKey => {
    const model = this.getMessageModel(messageKey);
    const warning = model.mode === "warn";
    const info = model.mode === "recommend";
    const silent = model.mode === "silent";
    const icon = warning ? "exclamation triangle" : "info circle";

    return (
      <Unless condition={silent}>
        <Message
          icon
          warning={warning}
          info={info}
          className="margin top medium"
        >
          <Icon name={icon} />
          <Message.Content>
            <Message.Header>Hinweis</Message.Header>
            <p>{this.renderMessage(messageKey)}</p>
          </Message.Content>
        </Message>
      </Unless>
    );
  };

  renderAdditionalProducts = (subLineItem, picker) => {
    if (this.hasAdditionalProducts(subLineItem)) {
      if (this.state.showUnacceptable) {
        return (
          <div>
            <Divider
              horizontal
              style={{ marginTop: "50px", marginBottom: "20px" }}
            >
              Weitere Produkte
            </Divider>
            {this.renderAdditionalProductsHint()}
            {this.renderUnacceptableCards(subLineItem, picker)}
          </div>
        );
      }
      return (
        <div>
          {this.renderFilterHint()}
          <Divider
            className="showUnacceptableProducts"
            horizontal
            style={{ marginTop: "20px", marginBottom: "20px" }}
          >
            <Button
              content="Alle Produkte anzeigen"
              onClick={this.showUnacceptableProducts}
            />
          </Divider>
        </div>
      );
    }
  };

  renderFilterHint = () => {
    return <Hint>{this.renderMessage("filterHint")}</Hint>;
  };

  renderAdditionalProductsHint = () => {
    return <Hint>{this.renderMessage("unavailable")}</Hint>;
  };

  renderMessage = messageKey => {
    const model = this.getMessageModel(messageKey);
    return (
      <FormattedMessage
        id={model.messageId}
        defaultValue={model.messageId}
        values={{
          ...model.values,
          ...defaultRichTextTags
        }}
      />
    );
  };

  getMessageModel = messageKey => {
    const { affectingSubLineItems, productLine } = this.props;
    if (affectingSubLineItems.length) {
      return {
        values: {
          option: affectingSubLineItems.map(sli => sli.title).join(", ")
        },
        messageId: `buyerPortal.roomBook.productOptions.messages.${messageKey}`
      };
    }
    if (productLine) {
      return {
        values: {
          type: productLine.name,
          type_plural: productLine.name_plural,
          series: productLine.selectedTagData
            .map(td => `${td.count} x ${td.name}`)
            .join(", ")
        },
        mode: productLine.mode,
        messageId:
          productLine.warning ||
          `buyerPortal.roomBook.productLine.messages.${messageKey}`
      };
    }

    /* Displaying the error that was sent to sentry
     * makes in-place-debugging much easier
     */
    const err = new Error(
      "A SubLineItem was tagged as invalid without a known reason (affectingSubLineItems/productLine)"
    );
    // eslint-disable-next-line no-console
    console.error(err);
    Sentry.captureException(err);

    return {
      values: {
        option: []
      },
      messageId: `buyerPortal.roomBook.productOptions.messages.${messageKey}`
    };
  };

  showUnacceptableProducts = () => {
    this.setState({ showUnacceptable: true });
  };

  hasAdditionalProducts = subLineItem => {
    return subLineItem.options.filter(o => !o.isAcceptableProduct).length > 0;
  };

  renderAcceptableCards = (subLineItem, picker) => {
    return this.renderCards(
      subLineItem,
      subLineItem.options.filter(
        o => o.isBuyerSelection || o.isAcceptableProduct
      ),
      picker
    );
  };

  renderUnacceptableCards = (subLineItem, picker) => {
    return this.renderCards(
      subLineItem,
      subLineItem.options.filter(
        o => !o.isAcceptableProduct && !o.isBuyerSelection
      ),
      picker
    );
  };

  renderCards = (subLineItem, options, picker) => {
    const {
      priceField,
      renderMissingMappingIcon,
      renderMetaThumbs,
      renderQuantityLabel
    } = this.props;
    const visibleOptions = options.filter(o => o.isVisible);
    return (
      <Card.Group
        data-component="cardGrid"
        itemsPerRow={3}
        stackable
        doubling
        className="tablet-two"
      >
        {this.sort(visibleOptions).map((productOption, idx) => {
          return (
            <ProductOption
              key={productOption.id}
              subLineItem={subLineItem}
              option={productOption}
              index={idx}
              handleSelectFn={picker.select}
              priceField={priceField}
              renderMissingMappingIcon={renderMissingMappingIcon}
              renderMetaThumbs={renderMetaThumbs}
              renderQuantityLabel={renderQuantityLabel}
              loading={
                picker.loadingId &&
                productOption.id &&
                picker.loadingId === productOption.id
              }
            />
          );
        })}
      </Card.Group>
    );
  };

  sort(options) {
    const { order } = this.state;
    return sortBy(options, option => {
      return order[option.id];
    });
  }

  render() {
    const { subLineItem } = this.props;
    return (
      <div
        data-component="productOptions"
        data-portal={window.initialProps.portal || ""}
      >
        <SubLineItemPicker key={subLineItem.id} subLineItem={subLineItem}>
          {picker => {
            return (
              <div>
                {!subLineItem.isValid &&
                  this.renderSelectionHint("incompatible")}
                {subLineItem.isDecided &&
                  !subLineItem.isRecommendable &&
                  this.renderSelectionHint("notRecommended")}
                {!subLineItem.isConfigurable &&
                  this.renderSelectionHint("missing")}

                {this.renderAcceptableCards(subLineItem, picker)}

                {this.renderAdditionalProducts(subLineItem, picker)}
              </div>
            );
          }}
        </SubLineItemPicker>
      </div>
    );
  }
}

const mapStateToProps = (state, props) => {
  const { i18n } = state;
  const { subLineItem } = props;

  const userRole = getUserRole(state);
  const affectingSubLineItems = getAffectingSubLineItems(state)(subLineItem);
  const productLine = getActivatedProductLine(state)(subLineItem.productTags);
  const subLineItemsById = getSubLineItemsById(state);

  return {
    i18n,
    userRole,
    affectingSubLineItems,
    productLine,
    subLineItemsById
  };
};

const mapDispatchToProps = (dispatch, props) => {
  const { id } = props.params;
  const LineItemResource = getResource(SUB_LINE_ITEM_RESOURCE);

  return {
    lineItemResource: new LineItemResource(dispatch, id)
  };
};

export default withRouter(
  connect(mapStateToProps, mapDispatchToProps)(ProductOptions)
);
